<template>
  <div :class="[$options.name, 'card']">
    <div class="collapse-table__toolbar">
      <Field v-if="searchable"
        v-model="searchQuery"
        class="collapse-table__search"
        type="search"
        size="sm"
        :icon="searchPending ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-magnifying-glass'"
        placeholder="Otsi..."
        aria-label="Otsi"
        @update:model-value="onSearchInput" />
      <!-- #actions: a list's own buttons, beside the add button. -->
      <span v-if="addable || $slots.actions"
        class="collapse-table__actions">
        <slot name="actions" />
        <btn v-if="addable"
          class-name="btn--sm"
          icon="fa-solid fa-plus"
          @click="startNew">{{ addLabel }}</btn>
      </span>
    </div>

    <div v-if="loading"
      class="collapse-table__loading">
      <span class="spinner"
        role="status"
        aria-label="Laadimine" />
    </div>

    <template v-else>
      <div v-if="!items.length && !isAdding"
        class="empty-state">
        <div class="empty-state-title">{{ emptyLabel }}</div>
      </div>

      <div v-else
        class="collapse-table__body"
        :class="{ 'is-busy': ordering || paging }">
        <div class="collapse-table__head">
          <div v-if="orderable"
            class="collapse-table__head-col collapse-table__head-col--handle" />
          <div v-for="col in columns"
            :key="col.key"
            class="collapse-table__head-col"
            :class="{
              'collapse-table__head-col--center': col.center,
              'collapse-table__head-col--sortable': col.sortable,
            }"
            :style="headerColStyle(col)"
            @click="col.sortable && toggleOrder(col.sortKey || col.key)">
            <slot :name="'head:' + col.key"
              :items="items">{{ col.label || col.key }}</slot>
            <i v-if="col.sortable && orderBy === (col.sortKey || col.key)"
              :class="orderDir === 'ASC' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" />
            <i v-else-if="col.sortable"
              class="fa-solid fa-sort collapse-table__sort-idle" />
          </div>
          <div v-if="expandable"
            class="collapse-table__head-col collapse-table__head-col--chev" />
        </div>

        <div class="accordion collapse-table__list">
          <!-- New-item panel. Desktop only; on mobile startNew opens the modal. -->
          <details v-if="isAdding && !isMobile"
            class="accordion-item"
            open>
            <summary class="accordion-summary"
              @click.prevent="cancel">
              <span class="collapse-table__new-title">{{ newTitle }}</span>
              <svg class="chev"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </summary>
            <div class="accordion-content">
              <div v-if="saving"
                class="collapse-table__saving">
                <span class="spinner spinner-sm" />
              </div>
              <slot name="form"
                :item="null"
                :is-new="true" />
            </div>
          </details>

          <!-- Always mounted, disabled when the list is not reorderable, so the
               row markup below exists in one copy rather than two. -->
          <VueDraggable v-model="items"
            handle=".collapse-table__drag-handle"
            :disabled="!orderable || !!activePanel"
            :animation="200"
            @end="onReorder">
            <details v-for="item in items"
              :key="item.id"
              class="accordion-item"
              :open="activePanel === item.id">
              <summary class="accordion-summary"
                @click.prevent="onSummaryClick(item)">
                <span v-if="orderable"
                  class="collapse-table__drag-handle"
                  @click.stop>
                  <i class="fa-solid fa-grip-vertical" />
                </span>
                <table-row :columns="columns"
                  :item="item">
                  <template v-for="col in columns"
                    :key="col.key"
                    #[col.key]="slotProps">
                    <slot :name="'col:' + col.key"
                      v-bind="slotProps" />
                  </template>
                </table-row>
                <svg v-if="expandable"
                  class="chev"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </summary>
              <div v-if="expandable && activePanel === item.id"
                class="accordion-content">
                <div v-if="saving"
                  class="collapse-table__saving">
                  <span class="spinner spinner-sm" />
                </div>
                <slot name="form"
                  :item="item"
                  :is-new="false" />
              </div>
            </details>
          </VueDraggable>
        </div>
      </div>

      <!-- Mobile form host. Below 640px rows open here instead of expanding. -->
      <Modal :open="isMobile && showMobileModal"
        @close="closeMobileModal">
        <template #header>
          <h2 class="collapse-table__modal-title">{{ mobileModalIsNew ? newTitle : mobileModalTitle }}</h2>
        </template>
        <div v-if="mobileModalItem || mobileModalIsNew"
          class="collapse-table__modal-body">
          <div v-if="saving"
            class="collapse-table__saving">
            <span class="spinner spinner-sm" />
          </div>
          <slot name="form"
            :item="mobileModalItem"
            :is-new="mobileModalIsNew" />
        </div>
      </Modal>

      <div v-if="total > 0"
        class="collapse-table__footer">
        <span class="collapse-table__count">
          {{ rangeStart }}–{{ rangeEnd }} / {{ total }}
        </span>

        <div class="pagination">
          <button class="page-btn"
            type="button"
            aria-label="Eelmine leht"
            :disabled="paging || currentPage === 1"
            @click="onPageChange(currentPage - 1)">
            <i class="fa-solid fa-chevron-left" />
          </button>
          <button v-for="p in visiblePages"
            :key="p"
            class="page-btn"
            type="button"
            :class="{ active: p === currentPage }"
            :aria-current="p === currentPage ? 'page' : undefined"
            :disabled="paging"
            @click="onPageChange(p)">{{ p }}</button>
          <button class="page-btn"
            type="button"
            aria-label="Järgmine leht"
            :disabled="paging || currentPage === totalPages"
            @click="onPageChange(currentPage + 1)">
            <i class="fa-solid fa-chevron-right" />
          </button>
        </div>

        <Field v-model="pageSize"
          class="collapse-table__size"
          type="select"
          size="sm"
          aria-label="Kirjeid lehel"
          :options="PAGE_SIZES"
          :clearable="false"
          :disabled="paging"
          @update:model-value="onPageSizeChange" />
      </div>
    </template>
  </div>
</template>

<script>
import TableRow from './row.vue'
import { VueDraggable } from 'vue-draggable-plus'

export default {
  name: 'collapse-table',
  emits: ['saved', 'removed', 'reorder', 'loaded'],
  components: {
    TableRow,
    VueDraggable
  },
  props: {
    modelClass: { type: Function, required: true },
    columns: { type: Array, required: true },
    findOptions: { type: Object, default: () => ({}) },
    addLabel: { type: String, default: 'Lisa' },
    emptyLabel: { type: String, default: 'Kirjed puuduvad.' },
    newTitle: { type: String, default: 'Uus kirje' },
    searchable: { type: Boolean, default: false },
    orderable: { type: Boolean, default: false },
    addable: { type: Boolean, default: true },
    clickRoute: { type: [Object, Function], default: null },
    addRoute: { type: [Object, String], default: null },
  },
  data() {
    return {
      items: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      PAGE_SIZES: [10, 25, 50, 100].map((n) => ({ id: n, name: `${n} / lehel` })),
      loading: true,
      saving: false,
      activePanel: null,
      isAdding: false,
      orderBy: null,
      orderDir: null,
      ordering: false,
      paging: false,
      searchQuery: '',
      searchTimer: null,
      searchPending: false,
      isMobile: false,
      mobileQuery: null,
      showMobileModal: false,
      mobileModalItem: null,
      mobileModalIsNew: false,
    }
  },
  mounted() {
    this.mobileQuery = window.matchMedia('(max-width: 640px)')
    this.isMobile = this.mobileQuery.matches
    this.mobileQuery.addEventListener('change', this.onMediaChange)
    this.load()
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer)
    if (this.mobileQuery) {
      this.mobileQuery.removeEventListener('change', this.onMediaChange)
    }
  },
  computed: {
    hasRowClick() {
      return !!this.clickRoute
    },
    hasAddClick() {
      return !!this.addRoute
    },
    // Rows only expand in place when there is a form to show: a list that
    // routes away on click has no inline panel, and mobile uses the modal.
    expandable() {
      return !this.hasRowClick && !this.isMobile
    },
    mobileModalTitle() {
      if (!this.mobileModalItem) return ''
      const nameCol = this.columns.find(c => c.key === 'name' || c.key === 'title' || c.key === 'label')
      return nameCol ? this.mobileModalItem[nameCol.key] || '' : ''
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    rangeStart() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.pageSize, this.total)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4)
        else start = Math.max(1, end - 4)
      }
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    }
  },
  methods: {
    onMediaChange(e) {
      this.isMobile = e.matches
      if (this.isMobile) {
        this.activePanel = null
      } else {
        this.showMobileModal = false
      }
    },
    // <summary> click is intercepted so `open` stays driven by activePanel
    // rather than by the browser's own toggle.
    onSummaryClick(item) {
      if (this.hasRowClick) {
        this.onRowClick(item)
        return
      }
      if (this.isMobile) {
        this.openMobileModal(item)
        return
      }
      this.activePanel = this.activePanel === item.id ? null : item.id
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    openMobileModal(item) {
      this.mobileModalItem = item
      this.mobileModalIsNew = false
      this.showMobileModal = true
    },
    closeMobileModal() {
      this.showMobileModal = false
      this.isAdding = false
      setTimeout(() => {
        this.mobileModalItem = null
        this.mobileModalIsNew = false
      }, 300)
    },
    headerColStyle(col) {
      if (col.width) return { flex: `0 0 ${col.width}` }
      if (col.flex) return { flex: col.flex }
      return {}
    },
    async toggleOrder(key) {
      if (this.ordering) return
      if (this.orderBy === key) {
        if (this.orderDir === 'ASC') {
          this.orderDir = 'DESC'
        } else {
          this.orderBy = null
          this.orderDir = null
        }
      } else {
        this.orderBy = key
        this.orderDir = 'ASC'
      }
      this.ordering = true
      this.currentPage = 1
      await this.load()
      this.ordering = false
    },
    async load() {
      if (!this.items.length) this.loading = true
      try {
        const params = { ...this.findOptions.params }
        if (this.orderBy) {
          params.order = (this.orderDir === 'DESC' ? '-' : '') + this.orderBy
        }
        if (this.searchQuery) {
          params.search = this.searchQuery
        }
        const result = await new this.modelClass().find({
          asPlainData: true,
          ...this.findOptions,
          params,
          limit: this.pageSize,
          page: this.currentPage,
        })
        if (result && typeof result.total === 'number') {
          this.items = result.data
          this.total = result.total
        } else {
          this.items = result
          this.total = 0
        }
        this.$emit('loaded', this.items)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    startNew() {
      if (this.hasAddClick) {
        this.$router.push(this.addRoute)
        return
      }
      this.isAdding = true
      if (this.isMobile) {
        this.mobileModalItem = null
        this.mobileModalIsNew = true
        this.showMobileModal = true
      } else {
        this.activePanel = 'new'
      }
    },
    onRowClick(item) {
      const route = typeof this.clickRoute === 'function' ? this.clickRoute(item) : this.clickRoute
      this.$router.push(route)
    },
    cancel() {
      this.isAdding = false
      this.activePanel = null
      this.showMobileModal = false
      this.mobileModalItem = null
      this.mobileModalIsNew = false
    },
    async save(item, data) {
      this.saving = true
      try {
        const type = !item ? 'create' : 'update'

        if (!item) {
          await new this.modelClass(data).create()
        } else {
          await new this.modelClass({ id: item.id, ...data }).update()
        }

        this.$success(type === 'create' ? 'Kirje lisatud!' : 'Kirje salvestatud!')
        this.$emit('saved')

        this.cancel()
        await this.load()
      } catch (e) {
        this.$error('Salvestamisel tekkis viga: ' + (e.response?.data?.error || e.message))
      } finally {
        this.saving = false
      }
    },
    async autoSave(item, data) {
      this.saving = true
      try {
        await new this.modelClass({ id: item.id }).update({ payload: data })
        await this.load()
      } catch (e) {
        this.$error('Salvestamisel tekkis viga: ' + (e.response?.data?.error || e.message))
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      this.saving = true
      try {
        await new this.modelClass({ id: item.id }).delete()

        this.$success('Kirje kustutatud!')
        this.$emit('removed')

        this.cancel()
        await this.load()
      } catch (e) {
        this.$error(e.response?.data || e.message)
      } finally {
        this.saving = false
      }
    },
    onPageChange(page) {
      if (this.paging || page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      this.paging = true
      this.load().finally(() => { this.paging = false })
    },
    onPageSizeChange() {
      if (this.paging) return
      this.currentPage = 1
      this.paging = true
      this.load().finally(() => { this.paging = false })
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchPending = true
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1
        this.paging = true
        this.load().finally(() => {
          this.paging = false
          this.searchPending = false
        })
      }, 1000)
    },
    onReorder() {
      const ids = this.items.map(i => i.id)
      this.$emit('reorder', ids)
    }
  }
}
</script>

<style lang="scss">
// Compound selector on purpose: utilities.scss's .card loads after SFC styles
// (see main.js), so a bare .collapse-table would lose its padding reset.
.collapse-table.card {
  padding: 0;
  overflow: hidden;
}

.collapse-table__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-color-light);
}

.collapse-table__search {
  width: 260rem;
}

.collapse-table__actions {
  display: flex;
  gap: 8rem;
}

.collapse-table__loading {
  display: flex;
  justify-content: center;
  padding: var(--space-7);
}

.collapse-table__body.is-busy {
  opacity: 0.55;
  pointer-events: none;
}

// Column headers. These line up with .ak-table-row's cells, so the padding and
// gap have to match what gentelella's .accordion-summary applies to the row.
.collapse-table__head {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: 8rem 16rem;
  font-size: 11rem;
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.3rem;
  color: var(--text-muted);
  background: var(--bg-surface-secondary);
  border-bottom: 1px solid var(--border-color);
}

.collapse-table__head-col {
  flex: 1;
  min-width: 0;

  &--handle {
    flex: 0 0 20rem;
  }

  // Reserves the width of .accordion-summary's chevron so headers stay aligned
  // with the cells beneath them.
  &--chev {
    flex: 0 0 14rem;
  }

  &--center {
    text-align: center;
    display: flex;
    justify-content: center;
  }

  &--sortable {
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);

    &:hover {
      color: var(--text);
    }

    i {
      font-size: 10rem;
    }
  }
}

.collapse-table__sort-idle {
  opacity: 0.3;
}

// gentelella's .accordion is a gapped stack of bordered cards. For a dense
// admin list, collapse it back into flush rows — the accordion mechanics and
// tokens stay, only the separation changes.
.collapse-table__list.accordion {
  gap: 0;

  .accordion-item {
    border-width: 0 0 1px;
    border-radius: 0;
    border-color: var(--border-color-light);

    &:last-child {
      border-bottom: 0;
    }

    // gentelella tints the item's border on hover and on open. That reads as a
    // card treatment; on flush rows it just lights up one separator line. Pin
    // the border and highlight the row itself instead.
    &:hover,
    &[open] {
      border-color: var(--border-color-light);
    }
  }

  .accordion-summary:hover {
    background: var(--bg-surface-secondary);
  }

  .accordion-item[open] > .accordion-summary {
    background: var(--bg-surface-secondary);
  }

  .accordion-summary {
    padding: 10rem 16rem;
    gap: var(--space-5);
  }

  // The row sits between the drag handle and the chevron and takes what's
  // left, so its columns line up with .collapse-table__head above.
  .accordion-summary .ak-table-row {
    flex: 1;
    min-width: 0;
  }

  .accordion-content {
    padding: var(--space-4);
    background: var(--bg-surface-secondary);
    border-top: 1px solid var(--border-color-light);
    position: relative;
  }
}

.collapse-table__new-title {
  flex: 1;
  font-weight: var(--font-weight-bold);
  color: var(--text);
}

.collapse-table__drag-handle {
  flex: 0 0 20rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: var(--text-disabled);
  font-size: 13rem;

  &:active {
    cursor: grabbing;
  }
}

.collapse-table .sortable-ghost {
  opacity: 0.4;
}

.collapse-table__saving {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  // Theme-agnostic scrim: follows whatever surface it covers.
  background: color-mix(in srgb, var(--bg-surface) 65%, transparent);
}

.collapse-table__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid var(--border-color-light);
}

.collapse-table__count {
  font-size: 12rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.collapse-table__size {
  width: 150rem;
}

.collapse-table__modal-title {
  font-size: 20rem;
  font-weight: 700;
  color: var(--primary);
  text-align: center;
  margin: 0;
}

.collapse-table__modal-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding-bottom: 72rem;
}

.collapse-table__modal-body .collapse-table__modal-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-surface);
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -2rem 8rem rgba(0, 0, 0, 0.06);
  z-index: 10;
}

.collapse-table__date {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 12rem;
  font-variant-numeric: tabular-nums;

  > span {
    background: var(--bg-surface-secondary);
    border-radius: var(--radius-sm);
    padding: 2rem 5rem;
    color: var(--text-secondary);
    font-weight: 500;
    letter-spacing: 0.5rem;
  }
}

@include max-screen(640) {
  .collapse-table__search {
    width: 100%;
    max-width: 260rem;
  }

  .collapse-table__head {
    display: none;
  }

  // Rows open the modal on mobile, so the row itself is the whole target and
  // the disclosure chevron would be misleading.
  .collapse-table__list.accordion .accordion-summary {
    align-items: flex-start;

    .chev {
      display: none;
    }
  }

  .collapse-table__footer {
    justify-content: center;
  }
}
</style>
